import { FC } from 'react';
import { BarbellIcon, HeartIcon, ShieldCheckIcon, Icon } from '@phosphor-icons/react';
import Typhography from '../../../../shared/Typhography/Typhography';
import { useKeyboardActivate } from '../../../../hooks/useKeyboardActivate';
import { IOverviewHub } from './useOverviewHub';
import styles from './OverviewHub.module.scss';

export type OverviewHubTab = 'sforzo' | 'readiness' | 'afi';

interface IOverviewHubProps {
  hub: IOverviewHub;
  onGoTo: (tab: OverviewHubTab) => void;
}

const fmt = (v: number | null | undefined, digits = 0): string =>
  v == null ? '—' : v.toFixed(digits);

interface CardProps {
  Icon: Icon;
  title: string;
  value: string;
  sub: string;
  /** Riga piccola con copertura/periodo (regola vault: dichiarare su cosa poggia la media). */
  meta: string;
  loading: boolean;
  onClick: () => void;
}

const HubCard: FC<CardProps> = ({ Icon, title, value, sub, meta, loading, onClick }) => {
  const activate = useKeyboardActivate(onClick);
  return (
    <div className={styles.card} onClick={onClick} {...activate}>
      <div className={styles.head}>
        <Icon size={16} className={styles.icon} />
        <Typhography size="medium-12" className={styles.title}>
          {title}
        </Typhography>
      </div>
      {loading ? (
        <span className={styles.skeleton} aria-label="Caricamento" />
      ) : (
        <>
          <p className={styles.value}>{value}</p>
          <p className={styles.sub}>{sub}</p>
          <p className={styles.meta}>{meta}</p>
        </>
      )}
    </div>
  );
};

/**
 * Hub cliccabile in cima alla Panoramica squadra (#18): un titolare per ciascun
 * modulo recente ATTIVO (Sforzo/ACWR, Readiness di oggi, Antifragilità) che porta
 * al tab di dettaglio. Mostra solo le card dei tab visibili (module-visibility).
 * La panoramica a 14 giorni resta sotto invariata.
 */
const OverviewHub: FC<IOverviewHubProps> = ({ hub, onGoTo }) => {
  const { visible, loading, effort, readiness, afi } = hub;
  if (!visible.sforzo && !visible.readiness && !visible.afi) return null;

  return (
    <div className={styles.hub}>
      {visible.sforzo && (
        <HubCard
          Icon={BarbellIcon}
          title="Carico & ACWR"
          value={effort ? `${fmt(effort.avgLoad)} AU` : '—'}
          sub={effort ? `ACWR ${fmt(effort.avgAcwr, 2)} · ${effort.outOfRangeCount} fuori range` : '—'}
          meta={effort ? `settimana · ${effort.withData} atleti` : 'settimana corrente'}
          loading={loading.sforzo}
          onClick={() => onGoTo('sforzo')}
        />
      )}
      {visible.readiness && (
        <HubCard
          Icon={HeartIcon}
          title="Readiness oggi"
          value={readiness ? fmt(readiness.riMean, 1) : '—'}
          sub={readiness ? `${readiness.withData}/${readiness.total} compilati` : '—'}
          meta="oggi"
          loading={loading.readiness}
          onClick={() => onGoTo('readiness')}
        />
      )}
      {visible.afi && (
        <HubCard
          Icon={ShieldCheckIcon}
          title="Antifragilità"
          value={afi ? fmt(afi.mean, 1) : '—'}
          sub={afi ? `${afi.atRisk} a rischio` : '—'}
          meta={afi ? `${afi.withData} atleti` : 'AFI medio squadra'}
          loading={loading.afi}
          onClick={() => onGoTo('afi')}
        />
      )}
    </div>
  );
};

export default OverviewHub;
